import clsx from "clsx";
import { forwardRef } from "react";
import { motion } from "framer-motion";
import { StrapiMedia } from "@/components/shared/StrapiMedia";
import { FlowArrow } from "./FlowArrow";
import {Room} from "@/types/api";

type RoomOptionProps = {
    room: Room;
    isSelected: boolean;
    onSelect: () => void;
    isTransitioning?: boolean;
    imageRef?: React.Ref<HTMLDivElement>;
    direction: "left" | "right";
    showTopArrow?: boolean;
    showImage?: boolean;
    traceTopArrow?: boolean;
    traceDelay?: number;
    traceDuration?: number;
    onTraceComplete?: () => void;
    fadeOutDuration?: number;
    arrowHeight?: number;
};

export const ROOM_IMAGE_SIZE = 150;

const STEM_OFFSET = 10;
const HORIZONTAL_LENGTH = ROOM_IMAGE_SIZE / 2 + STEM_OFFSET;
const FIXED_SEGMENT_LENGTH = 30;
export const ARROW_GAP = 8;
const DEFAULT_ARROW_HEIGHT = 80;

const FADE_DURATION = 0.3;

export const RoomOption = forwardRef<HTMLDivElement, RoomOptionProps>(function RoomOption(
    {
        room,
        isSelected,
        onSelect,
        isTransitioning = false,
        imageRef,
        direction,
        showTopArrow = false,
        showImage = true,
        traceTopArrow = false,
        traceDelay = 0,
        traceDuration = 0.8,
        onTraceComplete,
        fadeOutDuration = FADE_DURATION,
        arrowHeight = DEFAULT_ARROW_HEIGHT,
    },
    ref,
) {
    const showTop = (isSelected || showTopArrow) && !isTransitioning;
    const showBottom = isSelected && !isTransitioning;

    const topArrowDirection = direction;
    const bottomArrowDirection = direction === "left" ? "right" : "left";

    const topAnchor = direction === "left" ? { left: "50%" } : { right: "50%" };
    const bottomAnchor = direction === "left" ? { left: "50%" } : { right: "50%" };

    const getArrowTransition = (visible: boolean) => ({
        duration: !visible && isTransitioning ? fadeOutDuration : FADE_DURATION,
    });

    return (
        <div ref={ref} className="relative flex flex-col items-center overflow-visible">
            {/* Top arrow */}
            <motion.div
                className="absolute pointer-events-none"
                style={{
                    bottom: `calc(100% + ${ARROW_GAP}px)`,
                    ...topAnchor,
                }}
                animate={{ opacity: showTop ? 1 : 0 }}
                transition={getArrowTransition(showTop)}
            >
                <FlowArrow
                    className="text-highlight-500"
                    height={arrowHeight}
                    horizontalLength={HORIZONTAL_LENGTH}
                    direction={topArrowDirection}
                    stretch="stem"
                    fixedSegmentLength={FIXED_SEGMENT_LENGTH}
                    visible={traceTopArrow}
                    traceDelay={traceDelay}
                    traceDuration={traceDuration}
                    onTraceComplete={onTraceComplete}
                />
            </motion.div>

            {/* Image + checkbox */}
            <button onClick={onSelect} className="flex flex-col items-center gap-3 cursor-pointer">
                <motion.div
                    ref={imageRef}
                    className="relative overflow-hidden"
                    style={{
                        width: ROOM_IMAGE_SIZE,
                        height: ROOM_IMAGE_SIZE,
                        clipPath: "url(#room-mask-obbox)",
                    }}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{
                        opacity: showImage ? 1 : 0,
                        scale: showImage ? 1 : 0.95,
                    }}
                    transition={{ duration: 0.3 }}
                >
                    <StrapiMedia size="lg" image={room.image} alt={room.name} className="h-full w-full object-cover" />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: isTransitioning ? 0 : showImage ? 1 : 0 }}
                    transition={{ duration: 0.25 }}
                    className={clsx(
                        "flex h-5 w-5 items-center justify-center rounded-full border-2",
                        isSelected ? "border-highlight-500/70 bg-highlight-500" : "border-secondary-500",
                    )}
                >
                    {isSelected && (
                        <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                    )}
                </motion.div>
            </button>

            {/* Bottom arrow (fade only via parent wrapper, no trace) */}
            <motion.div
                className="absolute pointer-events-none"
                style={{
                    top: `calc(100% + ${ARROW_GAP}px)`,
                    ...bottomAnchor,
                }}
                animate={{ opacity: showBottom ? 1 : 0 }}
                transition={getArrowTransition(showBottom)}
            >
                <FlowArrow
                    className="text-highlight-500"
                    height={arrowHeight}
                    horizontalLength={HORIZONTAL_LENGTH}
                    direction={bottomArrowDirection}
                    stretch="drop"
                    fixedSegmentLength={FIXED_SEGMENT_LENGTH}
                    traceDuration={0}
                />
            </motion.div>
        </div>
    );
});
